import React, { useState } from "react"
import { useDropzone } from "react-dropzone"
import { isMobile } from "react-device-detect"

import cx from "classnames"
import { useDispatch } from "react-redux"
import { uploadImage } from "@/store/features/genericSlice"
// import { log } from "@Services/logger.service"

import Button from "@/components/core/Button/Button"
import Checkbox from "@/components/core/Checkbox/Checkbox"
import closeIcon from "@/public/icons/close.svg"
import PdfIcon from "@/public/icons/pdf-grey.svg"
import { addDataLayer } from "../analytics"

const UploadDocument = props => {
  const dispatch = useDispatch()
  const [errors, setErrors] = useState([])
  const [status, setStatus] = useState("")
  const [isChecked, setIsChecked] = useState(false)
  const [isUploadClick, setIsUploadClick] = useState(false)
  const { imageList = null, updateList, i18n, data, setData, formTitle } = props

  const onDrop = async (acceptedFiles, rejectedFiles) => {
    const errList = rejectedFiles[0]?.errors.map(el => el.message)
    const isType = `${isUploadClick ? "upload file" : "drag and drop"}`
    errList && setErrors(errList)
    if (!errList && errors?.length > 0) setErrors([])
    if (errList) {
      if (rejectedFiles[0]?.errors[0]?.code === "file-invalid-type") {
        addDataLayer({
          title: `upload business documentation`,
          isUpload: true,
          uploadedDocs: [...imageList],
          sts: "failure",
          msg: i18n?.errorFileType?.toLowerCase(),
          formTitle,
          isType,
        })
        setErrors([i18n.errorFileType])
      }
    } else {
      if (imageList?.length + acceptedFiles?.length <= 5) {
        setErrors([])
        try {
          const newImageList = await Promise.all(
            acceptedFiles?.map(async f => {
              const res = await dispatch(uploadImage(f)).unwrap()
              return Object.assign(f, {
                preview: URL.createObjectURL(f),
                comment: "",
                link: res?.path,
                textCount: 0,
              })
            })
          )
          updateList([...imageList, ...newImageList])
          addDataLayer({
            title: `upload business documentation`,
            isUpload: true,
            uploadedDocs: [...imageList, ...newImageList],
            sts: "success",
            formTitle,
            isType,
          })
        } catch (err) {
          // log.error(err)
        }
      } else {
        addDataLayer({
          title: `upload business documentation`,
          isUpload: true,
          uploadedDocs: [...imageList],
          sts: "failure",
          msg: i18n?.uploadCountLimit?.toLowerCase(),
          formTitle,
          isType,
        })
        setErrors([i18n.uploadCountLimit])
      }
    }
    if (rejectedFiles?.length > 5 || rejectedFiles[0]?.file?.size > 5e6) {
      rejectedFiles?.length > 5
        ? setErrors([i18n.uploadCountLimit])
        : setErrors([i18n.errorMaxFileSizeLimit])
    }
    setIsUploadClick(false)
  }

  const validator = file => {
    if (file?.size > 5e6) {
      return {
        message: i18n.errorMaxFileSizeLimit,
      }
    }
    switch (file?.type) {
      case "image/jpeg":
      case "application/pdf":
        break
      default:
        return {
          message: i18n.errorFileType,
        }
    }
  }

  const { getRootProps, getInputProps } = useDropzone({
    accept: "image/jpeg, application/pdf",
    maxFiles: 5,
    multiple: true,
    onDrop,
    validator,
  })

  const onAddComment = (v, id) => {
    let newList = imageList
    newList[id]["comment"] = v
    newList[id]["textCount"] = v?.length
    updateList([...newList])
  }

  const onDelete = id => {
    const newList = imageList.filter(l => l !== id)
    updateList(newList)
    setStatus(i18n.ariaLabels.imageRemove)
  }

  const handleFocus = e => {
    e.currentTarget.classList.add("active-comment")
  }

  const handleBlur = e => {
    e.currentTarget.classList.remove("active-comment")
  }

  const truncateFileName = (fileName, maxLength = 20) => {
    if (!fileName) return "";
    
    const parts = fileName.split(".");
    if (parts.length < 2) return fileName;
    
    const extension = parts.pop();
    const baseName = parts.join(".");
    
    if (baseName.length <= maxLength) return fileName;
    
    const visibleLength = maxLength - 3; // Reserve space for "..."
    const start = baseName.slice(0, visibleLength / 2);
    const end = baseName.slice(-visibleLength / 2);
    
    return `${start}...${end}.${extension}`;
  };

  return (
    <div className="optional-form">
      <h3 className="question">{i18n.uploadDoc}</h3>
      <p className="description">{i18n.attachDoc}</p>
      <ul className="description-list">
        <li>
          <span>{i18n.designCert}</span>
        </li>
        <li>
          <span>{i18n.designMembership}</span>
        </li>
        <li>
          <span>{i18n.businessLicense}</span>
        </li>
      </ul>
      <div className="upload-notes">
        <p className="upload-notes__supported-formats">
          <span className="upload-notes__supported-formats-description">{`${i18n.uploadFileTypes} `}</span>
          <span className="upload-notes__supported-formats-value">
            {i18n.uploadFormatType}
          </span>
        </p>
        <p className="upload-notes__supported-size">
          <span className="upload-notes__supported-size-description">
            {i18n.uploadSizeLimit}
          </span>
          <span className="upload-notes__supported-size-value">
            {i18n.uploadMBLimit}
          </span>
        </p>
      </div>
      <div className="asset-upload-tax-container">
        <div
          className={`upload-image-container ${
            imageList?.length < 6 && imageList?.length !== 0 ? "file-5" : ""
          }`}
          id="contact-image-preview-container"
          tabIndex="-1"
          role="presentation"
        >
          {imageList?.map((f, i) => (
            <div
              id="input-id-active"
              className="contact-us-image-preview"
              key={i}
              onClick={handleFocus}
              onBlur={handleBlur}
            >
              <div className="preview-wrapper">
                <img
                  className={cx({ "image-box": f.type !== "application/pdf" })}
                  src={f.type === "application/pdf" ? PdfIcon : f.preview}
                  alt={f.name.length < 12  ? f.name : truncateFileName(f.name,12)}
                />
              </div>
              <textarea
                id={`image-placeholder-${i}`}
                placeholder={i18n.commentBoxPlaceholder}
                value={f.comment}
                onChange={e => onAddComment(e.target.value, i)}
                maxLength={100}
              />
              <span className="text-count">{f.textCount} / 100</span>
              <span className="image-delete">
                <img
                  tabIndex="0"
                  className="custom-close-icone"
                  onClick={() => onDelete(f)}
                  src={closeIcon?.src}
                  alt="close"
                />
              </span>
            </div>
          ))}
          {imageList?.length !== 5 && (
            <div className="drag-and-drop-container" {...getRootProps()}>
              <input {...getInputProps()} />
              <div className="drag-and-drop-text-large">{i18n.dragAndDrop}</div>
              <div className="drag-and-drop-text-small">{i18n.orText}</div>
              <div className="drag-and-drop-upload-btn">
                <Button
                  role="button"
                  tabIndex="0"
                  type="ghost"
                  label={i18n.uploadFileButton}
                  onClick={() => setIsUploadClick(true)}
                  disabled={imageList?.length === 5 && isMobile}
                />
              </div>
            </div>
          )}
          <div role="region" aria-live="polite" className="aria-messages">
            {status}
          </div>
          {errors && (
            <p className="upload-image-container__error-msg">{errors[0]}</p>
          )}
        </div>
        <div className="tax-request-container">
          <p className="tax-request-footer__message">{i18n.taxMsg}</p>
          <Checkbox
            value={i18n.taxReqLabel}
            checked={isChecked}
            onChange={e => {
              setIsChecked(!isChecked)
              setData({ ...data, taxReq: !isChecked })
            }}
          />
        </div>
      </div>
    </div>
  )
}

export default UploadDocument
